import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input, Radio, Button, Space, Spin, message, Tag } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../../../../store";

// API & Types
import {
  ChecklistAnswerType,
  ConditionalAction,
} from "../../MasterData/checklistTypes";
import { getQuestionsListAPI } from "../../MasterData/checklistApis";
import { ChecklistFileUploadModal } from "../../Quotations/Components/ChecklistFileUploadModal";
import { formatToLocalDate } from "../../../../helpers/utils";
import { getDecryptedName } from "../../../../helpers/bycrypt";
import CommonFilePreviewModal from "../../../Components/CommonFilePreviewModal";
import { updateOrderChecklistAPI } from "../orderApis";
import { WarningSeverity } from "../../Quotations/quotationTypes";
import { WarningActionLayouts, WarningsModal } from "../../../../Components/WarningsModal";

const { TextArea } = Input;

interface ChecklistQuestion {
  _id: string;
  question_text: string;
  ans_type: ChecklistAnswerType;
  if_yes_action?: ConditionalAction | null;
  if_no_action?: ConditionalAction | null;
}

interface AnswerEntry {
  ans_type: ChecklistAnswerType;
  answer: string | boolean | string[] | null;
  conditional_answer?: string | string[] | null;
}

type ChecklistState = Record<string, AnswerEntry>;

interface UploadModalState {
  open: boolean;
  questionId: string | null;
  questionText: string;
  isConditional: boolean;
  ansType: ChecklistAnswerType;
}

interface OrderInstallationChecklistProps {
  data: any;
  onUpdateOrderDetails?: (updatedData: any) => void;
  setIsEditing?: (isEditing: boolean) => void;
}

export default function OrderInstallationChecklist({
  data,
  onUpdateOrderDetails,
  setIsEditing,
}: OrderInstallationChecklistProps) {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isEditFromUrl =
    searchParams.get("mode") === "edit" || searchParams.get("edit") === "true";

  const [isEditMode, setIsEditMode] = useState<boolean>(isEditFromUrl);

  useEffect(() => {
    if (isEditFromUrl) {
      setIsEditMode(true);
    }
  }, [isEditFromUrl]);

  useEffect(() => {
    if (setIsEditing) {
      setIsEditing(isEditMode);
    }
  }, [isEditMode, setIsEditing]);
  const [questions, setQuestions] = useState<ChecklistQuestion[]>([]);
  const [answers, setAnswers] = useState<ChecklistState>({});
  const [originalAnswers, setOriginalAnswers] = useState<ChecklistState>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Warnings Modal State
  const [apiWarnings, setApiWarnings] = useState<any[]>([]);
  const [apiWarningSeverity, setApiWarningSeverity] = useState<string>("");

  // File Preview Modal State
  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  // Upload Modal State
  const [uploadModal, setUploadModal] = useState<UploadModalState>({
    open: false,
    questionId: null,
    questionText: "",
    isConditional: false,
    ansType: ChecklistAnswerType.TEXT,
  });

  const existingAnswers = useMemo(
    () => data?.checklist_answers || [],
    [data?.checklist_answers]
  );

  const existingAnswersMap = useMemo(() => {
    const map: Record<string, any> = {};
    if (Array.isArray(existingAnswers)) {
      existingAnswers.forEach((item: any) => {
        if (item?.question_id) {
          map[item.question_id] = item;
        }
      });
    }
    return map;
  }, [existingAnswers]);

  // --- Fetch Master Questions ---
  const fetchMasterList = useCallback(() => {
    setLoading(true);
    dispatch(
      getQuestionsListAPI({
        filters: {},
        onSuccess: (res: any) => {
          const masterQuestions: ChecklistQuestion[] = res?.data || [];
          setQuestions(masterQuestions);

          const hydratedAnswers: ChecklistState = {};
          masterQuestions.forEach((q) => {
            const existing = existingAnswersMap[q._id];
            hydratedAnswers[q._id] = {
              ans_type: q.ans_type,
              answer: existing ? existing.answer : null,
              conditional_answer: existing ? existing.conditional?.value || null : null,
            };
          });

          setAnswers(hydratedAnswers);
          setOriginalAnswers(JSON.parse(JSON.stringify(hydratedAnswers)));
          setLoading(false);
        },
        onError: () => {
          message.error("Failed to load checklist questions");
          setLoading(false);
        },
      })
    );
  }, [dispatch, existingAnswersMap]);

  useEffect(() => {
    fetchMasterList();
  }, [fetchMasterList]);

  // --- Handlers ---
  const handleAnswerChange = (
    questionId: string,
    type: ChecklistAnswerType,
    value: any,
    isConditional: boolean = false
  ) => {
    setAnswers((prev) => {
      const currentEntry = prev[questionId] || { ans_type: type, answer: null };
      if (isConditional) {
        return {
          ...prev,
          [questionId]: { ...currentEntry, conditional_answer: value },
        };
      }
      const newState = {
        ...prev,
        [questionId]: { ...currentEntry, answer: value },
      };
      if (type === ChecklistAnswerType.YES_NO) {
        newState[questionId].conditional_answer = null;
      }
      return newState;
    });
  };

  const handleOpenUpload = (
    question: ChecklistQuestion,
    isConditional: boolean
  ) => {
    setUploadModal({
      open: true,
      questionId: question._id,
      questionText: question.question_text,
      isConditional: isConditional,
      ansType: question.ans_type,
    });
  };

  const handleCloseUpload = () => {
    setUploadModal((prev) => ({ ...prev, open: false }));
  };

  const handleSaveUpload = (fileKey: string, fileName?: string) => {
    const { questionId, isConditional, ansType } = uploadModal;
    if (!questionId) return;

    const currentEntry = answers[questionId];
    const currentFiles = isConditional
      ? (currentEntry?.conditional_answer as string[]) || []
      : (currentEntry?.answer as string[]) || [];

    const newFiles = Array.isArray(currentFiles)
      ? [...currentFiles, fileKey]
      : [fileKey];

    handleAnswerChange(questionId, ansType, newFiles, isConditional);
    handleCloseUpload();
  };

  const handleRemoveFile = (
    questionId: string,
    fileKeyToRemove: string,
    isConditional: boolean,
    ansType: ChecklistAnswerType
  ) => {
    const currentEntry = answers[questionId];
    if (!currentEntry) return;

    const currentFiles = isConditional
      ? (currentEntry.conditional_answer as string[])
      : (currentEntry.answer as string[]);

    if (!Array.isArray(currentFiles)) return;

    const newFiles = currentFiles.filter((key) => key !== fileKeyToRemove);
    handleAnswerChange(questionId, ansType, newFiles, isConditional);
  };

  const getCleanFileName = (url: string) => {
    try {
      const parts = url.split("/");
      const fullName = parts[parts.length - 1];
      if (fullName.includes("_") && fullName.length > 37) {
        return fullName.substring(fullName.indexOf("_") + 1);
      }
      return fullName;
    } catch (e) {
      return "File";
    }
  };

  const getFilledByText = (item: any) => {
    if (!item.updated_by) return "";
    const name = getDecryptedName(item.updated_by);
    const date = formatToLocalDate(item.updated_at, true);
    return `Filled By: ${name} on ${date}`;
  };

  const hasAnswerChanged = useCallback(
    (original: AnswerEntry | undefined, current: AnswerEntry | undefined) => {
      if (!original && !current) return false;
      if (!original || !current) return true;

      const normalize = (val: any) => (val === undefined ? null : val);

      const origAns = normalize(original?.answer);
      const currAns = normalize(current?.answer);

      const origCond = normalize(original?.conditional_answer);
      const currCond = normalize(current?.conditional_answer);

      // Compare Main Answer
      if (Array.isArray(origAns) || Array.isArray(currAns)) {
        const arr1 = Array.isArray(origAns) ? [...origAns].sort() : [];
        const arr2 = Array.isArray(currAns) ? [...currAns].sort() : [];
        if (JSON.stringify(arr1) !== JSON.stringify(arr2)) return true;
      } else if (origAns !== currAns) {
        return true;
      }

      // Compare Conditional Answer
      if (Array.isArray(origCond) || Array.isArray(currCond)) {
        const arr1 = Array.isArray(origCond) ? [...origCond].sort() : [];
        const arr2 = Array.isArray(currCond) ? [...currCond].sort() : [];
        if (JSON.stringify(arr1) !== JSON.stringify(arr2)) return true;
      } else if (origCond !== currCond) {
        return true;
      }

      return false;
    },
    [],
  );

  const executeSave = useCallback(
    (): Promise<boolean> => {
      const orderId = data?._id;
      if (!orderId) {
        message.error("Order ID is missing");
        return Promise.resolve(false);
      }

      return new Promise((resolve) => {
        const checklistPayload = questions.map((q) => {
          const currentData = answers[q._id] || {
            ans_type: q.ans_type,
            answer: null,
          };
          const originalData = originalAnswers[q._id];
          const isModified = hasAnswerChanged(originalData, currentData);

          const payloadItem: any = {
            question_id: q._id,
            question_text: q.question_text,
            ans_type: q.ans_type,
            if_yes_action: q.if_yes_action || null,
            if_no_action: q.if_no_action || null,
            answer: currentData.answer,
            is_modified: isModified,
          };

          if (q.ans_type === ChecklistAnswerType.YES_NO) {
            const isYes = currentData.answer === true;
            const actionType = isYes ? q.if_yes_action : q.if_no_action;
            payloadItem.conditional = {
              action_type: actionType || null,
              value:
                actionType === ConditionalAction.N_A || !actionType
                  ? null
                  : currentData.conditional_answer,
            };
          } else {
            payloadItem.conditional = { action_type: null, value: null };
          }
          return payloadItem;
        });

        const payload = {
          checklist_answers: checklistPayload,
        };

        dispatch(
          updateOrderChecklistAPI({
            order_id: orderId,
            payload,
            onSuccess: (resData: any) => {
              if (onUpdateOrderDetails) {
                onUpdateOrderDetails(resData);
              }
              const newOriginals: ChecklistState = {};
              checklistPayload.forEach((item: any) => {
                newOriginals[item.question_id] = {
                  ans_type: item.ans_type,
                  answer: item.answer,
                  conditional_answer: item.conditional?.value || null,
                };
              });
              setOriginalAnswers(newOriginals);
              setApiWarnings([]);
              setApiWarningSeverity("");
              setIsEditMode(false);
              resolve(true);
            },
            onWarnings: (warnings: any[], severity?: string) => {
              setApiWarnings(warnings);
              setApiWarningSeverity(severity || "");
              setSaving(false);
              resolve(false);
            },
            onError: () => {
              setSaving(false);
              resolve(false);
            },
          }),
        );
      });
    },
    [
      data?._id,
      questions,
      answers,
      originalAnswers,
      dispatch,
      hasAnswerChanged,
      onUpdateOrderDetails,
    ],
  );

  const handleSaveChecklist = async () => {
    setSaving(true);
    await executeSave();
    setSaving(false);
  };

  // --- Render Edit Controls ---
  const renderFileTags = (
    fileKeys: string[] | null | undefined,
    question: ChecklistQuestion,
    isConditional: boolean
  ) => {
    if (!fileKeys || !Array.isArray(fileKeys) || fileKeys.length === 0)
      return null;

    return (
      <div className="mt-2 d-flex flex-wrap gap-2">
        {fileKeys.map((key, index) => (
          <Tag
            key={index}
            closable
            onClose={(e) => {
              e.preventDefault();
              handleRemoveFile(
                question._id,
                key,
                isConditional,
                question.ans_type
              );
            }}
            closeIcon={
              <CloseCircleOutlined className="text-danger fs-6 ms-2" />
            }
            className="d-inline-flex align-items-center px-3 py-1 bg-white border rounded-2 fs-6 fw-semibold cursor-pointer"
            style={{
              border: "1px solid #E0E0E0",
              whiteSpace: "normal",
              wordBreak: "break-all",
              maxWidth: "100%",
              height: "auto",
            }}
          >
            <span
              onClick={() => {
                setPreviewFiles(fileKeys);
                setPreviewIndex(index);
                setPreviewModalOpen(true);
              }}
              title="Click to view file"
            >
              {getCleanFileName(key)}
            </span>
          </Tag>
        ))}
      </div>
    );
  };

  const renderConditionalInput = (question: ChecklistQuestion) => {
    const currentAnswer = answers[question._id]?.answer as boolean | undefined;
    if (currentAnswer === undefined || currentAnswer === null) return null;
    const action =
      currentAnswer === true ? question.if_yes_action : question.if_no_action;

    if (!action || action === ConditionalAction.N_A) return null;
    const subValue = answers[question._id]?.conditional_answer;

    switch (action) {
      case ConditionalAction.TEXT:
        return (
          <Input
            className="form-control mt-2"
            placeholder="Please specify details..."
            value={(subValue as string) || ""}
            onChange={(e) =>
              handleAnswerChange(
                question._id,
                question.ans_type,
                e.target.value,
                true
              )
            }
          />
        );
      case ConditionalAction.LONG_TEXT:
        return (
          <TextArea
            className="form-control mt-2"
            rows={3}
            placeholder="Please explain..."
            value={(subValue as string) || ""}
            onChange={(e) =>
              handleAnswerChange(
                question._id,
                question.ans_type,
                e.target.value,
                true
              )
            }
          />
        );
      case ConditionalAction.FILE_UPLOAD:
        return (
          <>
            <div className="mt-2 border-top pt-2 d-flex justify-content-end align-items-center gap-2">
              <Button
                type="primary"
                className="bg-dark border-0 rounded-2 px-4 fw-bold"
                onClick={() => handleOpenUpload(question, true)}
              >
                UPLOAD
              </Button>
            </div>
            {renderFileTags(subValue as string[], question, true)}
          </>
        );
      default:
        return null;
    }
  };

  const renderQuestionItem = (question: ChecklistQuestion) => {
    const entry = answers[question._id] || {};
    const val = entry.answer;
    switch (question.ans_type) {
      case ChecklistAnswerType.TEXT:
        return (
          <div
            key={question._id}
            className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2 d-flex flex-sm-row flex-column justify-content-between align-items-sm-center gap-2 bg-white"
          >
            <span className="fw-bold fs-6 lh-m">{question.question_text}</span>
            <Input
              className="form-control checklist-input"
              style={{ maxWidth: "300px" }}
              placeholder="Type answer..."
              value={(val as string) || ""}
              onChange={(e) =>
                handleAnswerChange(
                  question._id,
                  question.ans_type,
                  e.target.value
                )
              }
            />
          </div>
        );
      case ChecklistAnswerType.LONG_TEXT:
        return (
          <div
            key={question._id}
            className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2 bg-white"
          >
            <p className="fw-bold fs-6 lh-m mb-2">{question.question_text}</p>
            <TextArea
              rows={4}
              className="form-control"
              placeholder="Type details..."
              value={(val as string) || ""}
              onChange={(e) =>
                handleAnswerChange(
                  question._id,
                  question.ans_type,
                  e.target.value
                )
              }
            />
          </div>
        );
      case ChecklistAnswerType.YES_NO:
        return (
          <div
            key={question._id}
            className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2 bg-white"
          >
            <div className="d-flex flex-sm-row flex-column justify-content-between align-items-sm-center mb-0 w-100 gap-2">
              <span className="fw-bold fs-6 lh-m">
                {question.question_text}
              </span>
              <Radio.Group
                value={val}
                onChange={(e) =>
                  handleAnswerChange(
                    question._id,
                    question.ans_type,
                    e.target.value
                  )
                }
              >
                <Space direction="horizontal">
                  <Radio
                    value={true}
                    className="fs-6 text-primary customRadioButton"
                  >
                    Yes
                  </Radio>
                  <Radio
                    value={false}
                    className="fs-6 text-primary customRadioButton"
                  >
                    No
                  </Radio>
                </Space>
              </Radio.Group>
            </div>
            {renderConditionalInput(question)}
          </div>
        );
      case ChecklistAnswerType.FILE_UPLOAD:
        return (
          <div
            key={question._id}
            className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2 bg-white"
          >
            <div className="d-flex flex-sm-row flex-column justify-content-between align-items-sm-center mb-0 w-100 gap-2">
              <span className="fw-bold fs-6 lh-m">
                {question.question_text}
              </span>
              <Button
                type="primary"
                className="bg-dark border-0 rounded-2 px-4 fw-bold"
                onClick={() => handleOpenUpload(question, false)}
              >
                UPLOAD
              </Button>
            </div>
            {renderFileTags(val as string[], question, false)}
          </div>
        );
      default:
        return null;
    }
  };

  // --- Render Answer Summary for View Mode ---
  const renderSummaryAnswer = (item: any) => {
    const type = item.ans_type;
    const answer = item.answer;
    const conditionalVal = item.conditional?.value;
    const isValid = (val: any) =>
      val !== null && val !== undefined && val !== "";

    const renderSummaryFiles = (urls: string[]) => {
      if (!Array.isArray(urls) || urls.length === 0) return null;
      return (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {urls.map((url: string, idx: number) => (
            <Tag
              key={idx}
              className="d-flex align-items-center gap-2 px-3 py-1 bg-white border rounded-2 fs-6 fw-bold cursor-pointer"
              style={{
                border: "1px solid #E0E0E0",
                whiteSpace: "normal",
                wordBreak: "break-all",
                maxWidth: "100%",
                height: "auto",
              }}
              onClick={() => {
                setPreviewFiles(urls);
                setPreviewIndex(idx);
                setPreviewModalOpen(true);
              }}
            >
              {getCleanFileName(url)}
            </Tag>
          ))}
        </div>
      );
    };

    switch (type) {
      case "TEXT":
      case "LONG_TEXT":
        return (
          <span className="fw-bold fs-6 text-dark">
            {isValid(answer) ? answer : "-"}
          </span>
        );
      case "YES_NO":
        let baseLabel = "-";
        if (answer === true) baseLabel = "Yes";
        if (answer === false) baseLabel = "No";

        if (
          typeof conditionalVal === "string" &&
          conditionalVal.trim() !== ""
        ) {
          return (
            <div>
              <span className="fw-bold fs-6 text-dark">{baseLabel}, </span>
              <span className="fw-bold fs-6 text-dark">{conditionalVal}</span>
            </div>
          );
        }

        if (Array.isArray(conditionalVal) && conditionalVal.length > 0) {
          return (
            <div>
              <span className="fw-bold fs-6 text-dark">{baseLabel}</span>
              {renderSummaryFiles(conditionalVal)}
            </div>
          );
        }

        return <span className="fw-bold fs-6 text-dark">{baseLabel}</span>;
      case "FILE_UPLOAD":
        if (Array.isArray(answer) && answer.length > 0) {
          return renderSummaryFiles(answer);
        }
        return <span className="fw-bold fs-6 text-dark">-</span>;
      default:
        return <span className="fw-bold fs-6 text-dark">-</span>;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-md-3 mb-2">
        <h4 className="fs-5 fw-bold text-black mb-0">Installation Checklist</h4>
        {!isEditMode && (
          <span
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={() => {
              setIsEditMode(true);
              if (questions.length === 0) fetchMasterList();
            }}
          >
            Modify
          </span>
        )}
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5 bg-white rounded-4 shadow-sm">
          <Spin size="large" tip="Loading checklist questions..." />
        </div>
      ) : isEditMode ? (
        /* Edit Mode Form */
        <div className="card border-0 shadow-sm rounded-4 bg-white p-md-4 p-3">
          <div className="d-flex flex-column gap-3 mb-4">
            {questions.map((q) => renderQuestionItem(q))}
          </div>

          <div className="row justify-content-center mt-4">
            <div className="col-sm-auto col-6">
              <Button
                className="btn btn-lg btn-outline-secondary px-4 h-auto w-100 text-uppercase"
                onClick={() => {
                  setIsEditMode(false);
                  setAnswers(JSON.parse(JSON.stringify(originalAnswers)));
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
            <div className="col-sm-auto col-6">
              <Button
                type="primary"
                className="btn btn-lg btn-primary px-4 h-auto w-100 text-uppercase"
                onClick={handleSaveChecklist}
                loading={saving}
              >
                Save Checklist
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* View Mode Summary Cards */
        <div className="d-flex flex-column gap-3">
          {questions.length > 0 ? (
            questions.map((q) => {
              const entry = answers[q._id];
              const existingMeta = existingAnswersMap[q._id];
              const item = {
                question_id: q._id,
                question_text: q.question_text,
                ans_type: q.ans_type,
                answer: entry?.answer,
                conditional: { value: entry?.conditional_answer },
                updated_by: existingMeta?.updated_by,
                updated_at: existingMeta?.updated_at,
              };

              return (
                <div
                  key={q._id}
                  className="card border-0 shadow-sm rounded-4 bg-white"
                >
                  <div className="card-body p-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-2 mb-2">
                      <div className="fw-normal text-dark fs-6" style={{ flex: 1 }}>
                        {q.question_text}
                      </div>
                      {existingMeta && (
                        <div className="text-949494 fs-7 text-md-end text-start">
                          {getFilledByText(existingMeta)}
                        </div>
                      )}
                    </div>

                    <div className="mt-1">{renderSummaryAnswer(item)}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white text-center text-muted">
              No checklist questions available.
            </div>
          )}
        </div>
      )}

      {/* File Upload Modal */}
      <ChecklistFileUploadModal
        open={uploadModal.open}
        title={uploadModal.questionText}
        onClose={handleCloseUpload}
        onSave={handleSaveUpload}
      />

      {/* File Preview Modal */}
      <CommonFilePreviewModal
        visible={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewFiles([]);
          setPreviewIndex(0);
        }}
        files={previewFiles}
        activeIndex={previewIndex}
        onActiveIndexChange={setPreviewIndex}
      />

      {/* API Warnings Modal */}
      <WarningsModal
        warnings={apiWarnings}
        onClose={() => {
          setApiWarnings([]);
          setApiWarningSeverity("");
        }}
        subtitle="Please review the warnings before proceeding"
        actions={
          apiWarningSeverity === WarningSeverity.BLOCKING
            ? WarningActionLayouts.Blocking(() => {
                setApiWarnings([]);
                setApiWarningSeverity("");
              })
            : WarningActionLayouts.BypassableSave(
                () => {
                  setApiWarnings([]);
                  setApiWarningSeverity("");
                },
                async () => {
                  setSaving(true);
                  const success = await executeSave();
                  setSaving(false);
                  if (success) {
                    setApiWarnings([]);
                    setApiWarningSeverity("");
                  }
                },
                saving,
              )
        }
      />
    </div>
  );
}
